import type { LinearRGB } from '../../core/color';
import type { Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { SURFACE, edgeIdFor } from '../mesh/attributes';

const FRAME = { origin: [0, 0, 0] as Vec3, yaw: 0 };

function style(color: LinearRGB, surface = SURFACE.cloth): VertexStyle {
  return { color, surface, edgeId: edgeIdFor(1700), strokeAngle: 0.1, extent: [0.35, 0.7] };
}

interface RingLevel {
  readonly y: number;
  readonly x: number;
  readonly depth: number;
  readonly half: number;
  readonly z?: number;
}

/** Closed, flat-shaded rings. Forward is local +X; breadth is local Z. */
export function rounded(
  builder: MeshBuilder,
  color: LinearRGB,
  levels: readonly RingLevel[],
  segments = 8
): void {
  const rings = levels.map((l) =>
    Array.from({ length: segments }, (_, i): Vec3 => {
      const angle = (i * Math.PI * 2) / segments;
      return [l.x + Math.cos(angle) * l.depth, l.y, (l.z ?? 0) + Math.sin(angle) * l.half];
    })
  );
  const s = style(color);
  builder.polygon(s, FRAME, rings[0]!);
  builder.polygon(s, FRAME, rings.at(-1)!.slice().reverse());
  for (let row = 0; row < rings.length - 1; row++) {
    for (let i = 0; i < segments; i++) {
      const next = (i + 1) % segments;
      builder.quad(s, FRAME, [
        rings[row]![i]!,
        rings[row + 1]![i]!,
        rings[row + 1]![next]!,
        rings[row]![next]!,
      ]);
    }
  }
}

export function box(b: MeshBuilder, color: LinearRGB, min: Vec3, max: Vec3): void {
  b.box(style(color), FRAME, min, max);
}

/** Narrow articulated legs blend into the chest and haunch, with a small fetlock and hoof. */
export function horseLeg(b: MeshBuilder, coat: LinearRGB, hoof: LinearRGB, hind: boolean): void {
  rounded(
    b,
    coat,
    [
      { y: -0.95, x: 0.035, depth: 0.039, half: 0.043 },
      { y: -0.868, x: 0.022, depth: 0.043, half: 0.047 },
      { y: -0.69, x: hind ? -0.014 : 0.018, depth: 0.035, half: 0.039 },
      { y: -0.46, x: hind ? -0.065 : 0.032, depth: 0.059, half: 0.056 },
      { y: -0.29, x: hind ? -0.035 : 0.008, depth: 0.073, half: 0.067 },
      { y: -0.1, x: 0.008, depth: hind ? 0.096 : 0.09, half: 0.085 },
      { y: 0, x: 0, depth: hind ? 0.097 : 0.075, half: 0.083 },
    ],
    6
  );
  rounded(
    b,
    hoof,
    [
      { y: -1.03, x: 0.035, depth: 0.095, half: 0.075 },
      { y: -1.007, x: 0.035, depth: 0.095, half: 0.075 },
      { y: -0.913, x: 0.018, depth: 0.065, half: 0.053 },
    ],
    6
  );
}
